import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Plus, 
  Pencil, 
  Trash,
  Spinner,
  BookOpen
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function toDirectImageUrl(url) {
  if (!url) return url;
  // Google Drive: /file/d/ID/view -> thumbnail
  const driveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (driveMatch) return `https://drive.google.com/thumbnail?id=${driveMatch[1]}&sz=w1280`;
  // Google Drive: open?id=ID
  const driveOpenMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (driveOpenMatch) return `https://drive.google.com/thumbnail?id=${driveOpenMatch[1]}&sz=w1280`;
  // Google Drive: uc?id=ID
  const driveUcMatch = url.match(/drive\.google\.com\/uc\?.*id=([a-zA-Z0-9_-]+)/);
  if (driveUcMatch) return `https://drive.google.com/thumbnail?id=${driveUcMatch[1]}&sz=w1280`;
  return url;
}

export function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail_url: '',
    estimated_duration: 60,
    product: '',
    target_roles: [],
    client_access: [],
    is_public: false,
    is_published: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [coursesRes, clientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/courses`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/clients`, { withCredentials: true })
      ]);
      setCourses(coursesRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCourse) {
        await axios.put(`${API_URL}/api/courses/${editingCourse.id}`, formData, {
          withCredentials: true
        });
      } else {
        await axios.post(`${API_URL}/api/courses`, formData, {
          withCredentials: true
        });
      }
      setDialogOpen(false);
      setEditingCourse(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving course:', error);
    }
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      title: course.title,
      description: course.description,
      thumbnail_url: course.thumbnail_url || '',
      estimated_duration: course.estimated_duration || 60,
      product: course.product || '',
      target_roles: course.target_roles || [],
      client_access: course.client_access || [],
      is_public: course.is_public || false,
      is_published: course.is_published !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Tem certeza que deseja excluir este curso?')) return;
    try {
      await axios.delete(`${API_URL}/api/courses/${courseId}`, {
        withCredentials: true
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting course:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      thumbnail_url: '',
      estimated_duration: 60,
      product: '',
      target_roles: [],
      client_access: [],
      is_public: false,
      is_published: true
    });
  };

  const toggleClientAccess = (clientId) => {
    setFormData(prev => ({
      ...prev,
      client_access: prev.client_access.includes(clientId)
        ? prev.client_access.filter(id => id !== clientId)
        : [...prev.client_access, clientId]
    }));
  };

  const toggleTargetRole = (role) => {
    setFormData(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  const getClientAccessLabel = (clientAccess, isPublic) => {
    if (isPublic) return 'Publico';
    if (!clientAccess || clientAccess.length === 0) return 'Todos os clientes';
    return clientAccess.map(id => clients.find(c => c.id === id)?.name || id).join(', ');
  };

  const TARGET_ROLE_OPTIONS = [
    { value: 'tecnoservice', label: 'Funcionarios Tecnoservice', desc: 'Equipe interna Tecnoservice' },
    { value: 'client_employee', label: 'Funcionarios dos Clientes', desc: 'Tecnicos e operadores das empresas' },
    { value: 'client_user', label: 'Clientes dos Clientes', desc: 'Usuarios finais (ex: lojistas)' },
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20">
          <Spinner size={48} className="animate-spin text-[#2C3E50]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div data-testid="admin-courses">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Gerenciamento de Cursos</h1>
            <p className="text-[#5C6B7A]">Crie e gerencie cursos de treinamento</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingCourse(null);
              resetForm();
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#27AE60] hover:bg-[#219653]" data-testid="new-course-btn">
                <Plus size={18} className="mr-2" />
                Novo Curso
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
              <div className="relative px-8 pt-7 pb-5 sticky top-0 bg-white z-10">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#27AE60] to-[#2C3E50]" />
                <DialogHeader>
                  <DialogTitle className="text-[#1A252F] text-xl font-bold tracking-tight">
                    {editingCourse ? 'Editar Curso' : 'Criar Novo Curso'}
                  </DialogTitle>
                  <p className="text-[#5C6B7A] text-sm mt-1">
                    {editingCourse ? 'Atualize as informacoes do curso abaixo' : 'Preencha os campos abaixo para criar um novo curso'}
                  </p>
                </DialogHeader>
              </div>
              <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5" data-testid="course-form">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-[#1A252F] font-semibold text-sm">
                    Titulo do Curso <span className="text-[#E74C3C]">*</span>
                  </Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Modo Supervisor - Produto X"
                    required
                    className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
                    data-testid="course-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-[#1A252F] font-semibold text-sm">
                    Descricao <span className="text-[#E74C3C]">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descreva o conteudo e os objetivos do curso..."
                    required
                    rows={4}
                    className="border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6] resize-none"
                    data-testid="course-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="thumbnail" className="text-[#1A252F] font-semibold text-sm">
                      URL da Imagem
                    </Label>
                    <Input
                      id="thumbnail"
                      value={formData.thumbnail_url}
                      onChange={(e) => setFormData({ ...formData, thumbnail_url: e.target.value })}
                      onBlur={(e) => {
                        const converted = toDirectImageUrl(e.target.value);
                        if (converted !== e.target.value) {
                          setFormData(prev => ({ ...prev, thumbnail_url: converted }));
                        }
                      }}
                      placeholder="Cole link da imagem ou Google Drive"
                      className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
                      data-testid="course-thumbnail-input"
                    />
                    {formData.thumbnail_url && (
                      <div className="mt-2 relative w-full h-24 rounded-lg overflow-hidden bg-[#F0F0F0] border border-[#E8ECF0]">
                        <img
                          src={toDirectImageUrl(formData.thumbnail_url)}
                          alt="Preview"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="duration" className="text-[#1A252F] font-semibold text-sm">
                      Duracao (minutos)
                    </Label>
                    <Input
                      id="duration"
                      type="number"
                      value={formData.estimated_duration}
                      onChange={(e) => setFormData({ ...formData, estimated_duration: parseInt(e.target.value) })}
                      className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F]"
                      data-testid="course-duration-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="product" className="text-[#1A252F] font-semibold text-sm">
                    Produto
                  </Label>
                  <Input
                    id="product"
                    value={formData.product}
                    onChange={(e) => setFormData({ ...formData, product: e.target.value })}
                    placeholder="Ex: TSCASH, Produto A, Produto B"
                    className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60] focus:ring-[#27AE60]/20 text-[#1A252F] placeholder:text-[#95A5A6]"
                    data-testid="course-product-input"
                  />
                  <p className="text-xs text-[#5C6B7A]">Nome do produto/equipamento para filtrar no catalogo</p>
                </div>

                {/* Target Roles - Público Alvo */}
                <div className="space-y-3">
                  <Label className="text-[#1A252F] font-semibold text-sm">
                    Publico Alvo
                  </Label>
                  <p className="text-xs text-[#5C6B7A]">Selecione quem pode acessar este curso. Se nenhum for selecionado, todos os perfis terao acesso.</p>
                  <div className="space-y-2">
                    {TARGET_ROLE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleTargetRole(opt.value)}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left border-2 transition-all duration-200 ${
                          formData.target_roles.includes(opt.value)
                            ? 'border-[#27AE60] bg-[#27AE60]/5'
                            : 'border-[#E8ECF0] hover:border-[#BDC3C7]'
                        }`}
                        data-testid={`target-role-${opt.value}`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          formData.target_roles.includes(opt.value)
                            ? 'bg-[#27AE60] border-[#27AE60]'
                            : 'border-[#BDC3C7]'
                        }`}>
                          {formData.target_roles.includes(opt.value) && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${formData.target_roles.includes(opt.value) ? 'text-[#27AE60]' : 'text-[#1A252F]'}`}>
                            {opt.label}
                          </p>
                          <p className="text-xs text-[#5C6B7A]">{opt.desc}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Client access - only show if client roles are targeted */}
                {(formData.target_roles.includes('client_employee') || formData.target_roles.includes('client_user') || formData.target_roles.length === 0) && (
                <div className="space-y-3">
                  <Label className="text-[#1A252F] font-semibold text-sm">
                    Restringir por Cliente
                  </Label>
                  <p className="text-xs text-[#5C6B7A]">Selecione quais empresas podem acessar. Se nenhuma for selecionada, todos os clientes terao acesso.</p>
                  {clients.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {clients.map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => toggleClientAccess(client.id)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border-2 transition-all duration-200 ${
                            formData.client_access.includes(client.id)
                              ? 'border-[#2980B9] bg-[#2980B9]/10 text-[#2980B9]'
                              : 'border-[#BDC3C7] text-[#5C6B7A] hover:border-[#5C6B7A]'
                          }`}
                          data-testid={`client-toggle-${client.id}`}
                        >
                          {client.name}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-[#95A5A6] italic">Nenhum cliente cadastrado.</p>
                  )}
                </div>
                )}

                {/* Public toggle */}
                <div className={`rounded-xl p-4 border-2 transition-colors ${
                  formData.is_public 
                    ? 'bg-[#E3F2FD] border-[#2980B9]/30' 
                    : 'bg-[#F8F9FA] border-[#E8ECF0]'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="is_public" className="text-[#1A252F] font-semibold text-sm cursor-pointer">
                        {formData.is_public ? 'Curso Publico (sem login)' : 'Requer login'}
                      </Label>
                      <p className="text-xs text-[#5C6B7A] mt-0.5">
                        {formData.is_public 
                          ? 'Qualquer pessoa pode ver este curso sem precisar de login' 
                          : 'Usuario precisa estar logado para acessar'}
                      </p>
                    </div>
                    <Switch
                      id="is_public"
                      checked={formData.is_public}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_public: checked })}
                      data-testid="course-public-switch"
                    />
                  </div>
                </div>

                <div className={`rounded-xl p-4 border-2 transition-colors ${
                  formData.is_published 
                    ? 'bg-[#E8F5E9] border-[#27AE60]/30' 
                    : 'bg-[#FFF3E0] border-[#E67E22]/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="published" className="text-[#1A252F] font-semibold text-sm cursor-pointer">
                        {formData.is_published ? 'Publicado' : 'Rascunho (nao visivel)'}
                      </Label>
                      <p className="text-xs text-[#5C6B7A] mt-0.5">
                        {formData.is_published 
                          ? 'Visivel no catalogo de cursos' 
                          : 'Curso nao sera visivel para nenhum usuario'}
                      </p>
                    </div>
                    <Switch
                      id="published"
                      checked={formData.is_published}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                      data-testid="course-published-switch"
                    />
                  </div>
                </div>
                <div className="flex gap-3 pt-2 pb-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1 h-11 border-[#BDC3C7] text-[#5C6B7A] hover:bg-[#F8F9FA] hover:text-[#1A252F] font-medium"
                    onClick={() => setDialogOpen(false)}
                    data-testid="cancel-course-btn"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 h-11 bg-[#27AE60] hover:bg-[#219653] text-white font-medium shadow-md shadow-[#27AE60]/20"
                    data-testid="save-course-btn"
                  >
                    {editingCourse ? 'Atualizar Curso' : 'Criar Curso'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-xl border border-[#2C3E50]/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8F9FA]">
                <TableHead className="font-semibold text-[#1A252F]">Curso</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Modulos</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Duracao</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Acesso</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Status</TableHead>
                <TableHead className="font-semibold text-[#1A252F] text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {courses.map((course) => (
                <TableRow key={course.id} data-testid={`course-row-${course.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#F8F9FA]">
                        {course.thumbnail_url ? (
                          <img src={course.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <BookOpen size={20} className="text-[#BDC3C7]" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-[#1A252F]">{course.title}</p>
                        <p className="text-xs text-[#5C6B7A] line-clamp-1">{course.description}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-[#5C6B7A]">{course.module_count || 0}</TableCell>
                  <TableCell className="text-[#5C6B7A]">{course.estimated_duration} min</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {course.is_public && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#2980B9]/10 text-[#2980B9]">Publico</span>}
                      {(course.target_roles || []).includes('tecnoservice') && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#2C3E50]/10 text-[#2C3E50]">TS</span>}
                      {(course.target_roles || []).includes('client_employee') && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#34495E]/10 text-[#34495E]">Func.</span>}
                      {(course.target_roles || []).includes('client_user') && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#27AE60]/10 text-[#27AE60]">Final</span>}
                      {(!course.target_roles || course.target_roles.length === 0) && !course.is_public && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#95A5A6]/10 text-[#95A5A6]">Todos</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      course.is_published !== false
                        ? 'bg-[#27AE60]/10 text-[#27AE60]' 
                        : 'bg-[#F39C12]/10 text-[#F39C12]'
                    }`}>
                      {course.is_published !== false ? 'Publicado' : 'Rascunho'}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/admin/courses/${course.id}/modules`}>
                        <Button variant="ghost" size="sm" title="Gerenciar Modulos">
                          <BookOpen size={18} />
                        </Button>
                      </Link>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(course)} data-testid={`edit-course-${course.id}`}>
                        <Pencil size={18} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(course.id)} className="text-[#E74C3C] hover:text-[#E74C3C]">
                        <Trash size={18} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {courses.length === 0 && (
            <div className="py-12 text-center text-[#5C6B7A]">
              Nenhum curso encontrado. Crie seu primeiro curso!
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default AdminCourses;
